import React from 'react';
import { useParams } from 'react-router-dom';
import ModuleRenderer from './ModuleRenderer';
import PerformanceHero from './modules/PerformanceHero';
import { useSanityData } from './hooks/useSanityData';
import { urlFor } from './sanityClient';

// -----------------------------------------------------------------------
// DynamicPage: resolves any slug to the right Sanity document type and
// renders it using the appropriate template.
//
// Priority order when multiple document types share a slug (unlikely):
//   1. page         → render modules[] array
//   2. performance  → render PerformanceHero module
//   3. opinion      → render opinion article layout
//   4. platformCourse → render course layout
//   5. festivalEvent  → render festival event layout
// -----------------------------------------------------------------------

const MULTI_QUERY = `{
  "page": *[_type == "page" && slug.current == $slug][0]{
    _id, title, slug, modules[] {
      ...,
      "performance": performance->{ _id, title, slug, poster, description, year, cast, videoUrl, photos, photoCredit, nextSlug, subtitle, premiereDate, author }
    }
  },
  "performance": *[_type == "performance" && slug.current == $slug][0]{
    _id, title, slug, subtitle, description, year, premiereDate,
    cast, poster, photos, photoCredit, videoUrl, nextSlug
  },
  "opinion": *[_type == "opinion" && slug.current == $slug][0]{
    _id, title, slug, author, source, publishDate, aboutShow, coverImage, body
  },
  "platformCourse": *[_type == "platformCourse" && slug.current == $slug][0]{
    _id, title, slug, year, poster, description, videoUrl, materials
  },
  "festivalEvent": *[_type == "festivalEvent" && slug.current == $slug][0]{
    _id, title, slug, eventDate, poster, description, photos
  }
}`;

export default function DynamicPage() {
  const { slug } = useParams();
  const { data, loading, error } = useSanityData(MULTI_QUERY, { slug });

  if (loading) {
    return <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span>Loading…</span>
    </div>;
  }

  if (error) {
    return <div className='container py-5'><p>Error loading content. Please try again.</p></div>;
  }

  // 1. Generic page with modules
  if (data?.page) {
    const page = data.page;
    const lang  = localStorage.getItem('language') || 'arm';
    const title = page.title ? (page.title[lang] || page.title.en || '') : '';
    return (
      <div>
        {(page.modules || []).map((mod, i) => (
          <ModuleRenderer key={mod._key || i} module={mod} />
        ))}
        {(!page.modules || page.modules.length === 0) && (
          <div className='container py-5'>
            <h1>{title}</h1>
            <p>This page has no content yet. Add modules in the Sanity Studio.</p>
          </div>
        )}
      </div>
    );
  }

  // 2. Performance
  if (data?.performance) {
    return <PerformanceHero performance={data.performance} />;
  }

  // 3. Opinion article
  if (data?.opinion) {
    return <OpinionArticlePage opinion={data.opinion} />;
  }

  // 4. Platform course
  if (data?.platformCourse) {
    return <PlatformCoursePage course={data.platformCourse} />;
  }

  // 5. Festival event
  if (data?.festivalEvent) {
    return <FestivalEventPage event={data.festivalEvent} />;
  }

  // Not found
  return (
    <div className='container py-5 text-center'>
      <h2>Page not found</h2>
      <p>The content you are looking for does not exist or has been moved.</p>
      <a href='/' className='btn btn-outline-dark mt-3'>Go home</a>
    </div>
  );
}

// -----------------------------------------------------------------------
// Inline sub-templates for document types that don't use modules
// -----------------------------------------------------------------------

function OpinionArticlePage({ opinion: o }) {
  const lang = localStorage.getItem('language') || 'arm';
  const t    = (field) => (o[field] ? (o[field][lang] || o[field].en || '') : '');
  const imgSrc = o.coverImage ? urlFor(o.coverImage).width(600).url() : '';

  return (
    <section className='main-container main-container-decoration opinion-page'>
      <div className='container'>
        <div className='row'>
          <div className='col-lg-8 offset-lg-2'>
            <h1 className='mt-4 mb-3'>{t('title')}</h1>
            {imgSrc && <img src={imgSrc} alt={t('title')} className='img-fluid mb-4' />}
            <div className='opinion-meta mb-3 text-muted'>
              {o.author      && <span className='me-3'>{o.author}</span>}
              {o.source      && <span className='me-3'>— {o.source}</span>}
              {o.publishDate && <span>{o.publishDate}</span>}
            </div>
            {t('aboutShow') && <p className='font-italic text-muted mb-4'>{t('aboutShow')}</p>}
            <div dangerouslySetInnerHTML={{ __html: t('body') }} />
          </div>
        </div>
      </div>
    </section>
  );
}

function PlatformCoursePage({ course: c }) {
  const lang = localStorage.getItem('language') || 'arm';
  const t    = (field) => (c[field] ? (c[field][lang] || c[field].en || '') : '');
  const imgSrc = c.poster ? urlFor(c.poster).width(600).url() : '';

  return (
    <section className='main-container main-container-decoration platform-course-page'>
      <div className='container'>
        <div className='row'>
          {imgSrc && (
            <div className='col-lg-5 col-md-12 mb-4'>
              <img src={imgSrc} alt={t('title')} className='img-fluid' />
            </div>
          )}
          <div className={`col-lg-${imgSrc ? 7 : 12} col-md-12`}>
            <h1>{t('title')}</h1>
            {c.year && <p className='text-muted'>{c.year}</p>}
            <div dangerouslySetInnerHTML={{ __html: t('description') }} />
            {t('materials') && (
              <div className='mt-4'>
                <h4>Materials</h4>
                <div dangerouslySetInnerHTML={{ __html: t('materials') }} />
              </div>
            )}
          </div>
        </div>
        {c.videoUrl && (
          <div className='row mt-4'>
            <div className='col-12'>
              <iframe
                src={c.videoUrl}
                title={t('title')}
                width='100%'
                height='500'
                frameBorder='0'
                allow='autoplay; encrypted-media'
                allowFullScreen
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function FestivalEventPage({ event: ev }) {
  const lang = localStorage.getItem('language') || 'arm';
  const t    = (field) => (ev[field] ? (ev[field][lang] || ev[field].en || '') : '');
  const imgSrc = ev.poster ? urlFor(ev.poster).width(600).url() : '';
  const photos = ev.photos || [];

  return (
    <section className='main-container main-container-decoration festival-event-page'>
      <div className='container'>
        <div className='row'>
          {imgSrc && (
            <div className='col-lg-5 col-md-12 mb-4'>
              <img src={imgSrc} alt={t('title')} className='img-fluid' />
            </div>
          )}
          <div className={`col-lg-${imgSrc ? 7 : 12} col-md-12`}>
            <h1>{t('title')}</h1>
            {t('eventDate') && <p className='text-muted'>{t('eventDate')}</p>}
            <div dangerouslySetInnerHTML={{ __html: t('description') }} />
          </div>
        </div>
        {photos.length > 0 && (
          <div className='row mt-4'>
            {photos.map((ph, i) => {
              const src = urlFor(ph).width(400).url();
              return (
                <div className='col-lg-4 col-md-6 mb-3' key={i}>
                  <img src={src} alt={`festival-photo-${i}`} className='img-fluid' />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
